import type {
  ChatAttachment,
  ChatDebugData,
  ChatMessage,
  ChatPlan,
  ChatQuestion,
  ChatRichTextArtefact,
  ChatTask,
  ChatThread,
  TokenResolutions,
} from "./types";

// Server-side payload shape for rich-text artefacts (no client-only fields
// like `id`, which the backend regenerates).
type RichTextArtefactPayload = Pick<ChatRichTextArtefact, "label" | "size_bytes" | "markdown">;

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute("content") || "";
}

// Carries the HTTP status so callers can react to specific failures (e.g. the
// chat store self-heals its persisted thread id on 404). Callers check the
// `status` property rather than instanceof, so mocked chatApi modules in tests
// can reject with any status-bearing Error.
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, statusText: string) {
    super(`HTTP ${status}: ${statusText}`);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText);
  }

  return response.json();
}

export const ACCEPTED_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
];
export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
export const MAX_FILES_PER_MESSAGE = 5;

export async function uploadFile(
  file: File,
): Promise<{ url: string; signed_id: string; filename: string }> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch("/uploads", {
    method: "POST",
    headers: { "X-CSRF-Token": getCsrfToken() },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }

  return response.json();
}

export async function fetchThreads(
  campaignId?: string,
): Promise<{ threads: ChatThread[]; session_id: string }> {
  const url = campaignId ? `/chat/threads?campaign_id=${campaignId}` : "/chat/threads";
  return request(url);
}

export async function fetchSuggestedPrompts(campaignId?: string): Promise<string[]> {
  const url = campaignId
    ? `/chat/suggested_prompts?campaign_id=${campaignId}`
    : "/chat/suggested_prompts";
  const data = await request<{ prompts: string[] }>(url);
  return data.prompts;
}

/** Thread state plus (a slice of) its transcript — the shape shared by a
 *  full load and an incremental resync. */
export interface ThreadSnapshot {
  thread: ChatThread;
  messages: ChatMessage[];
  active_plan: ChatPlan | null;
  pending_question: ChatQuestion | null;
  task_list: ChatTask[];
}

export async function fetchThread(threadId: string): Promise<ThreadSnapshot> {
  return request(`/chat/threads/${threadId}`);
}

// Incremental catch-up: the thread's current state and every message after
// the given message id (all messages when no cursor). Used after a lost
// socket, a broadcast seq gap, tab focus, or a stalled turn.
export async function fetchMessagesAfter(
  threadId: string,
  after?: string,
): Promise<ThreadSnapshot> {
  const query = after ? `?after=${encodeURIComponent(after)}` : "";
  return request(`/chat/threads/${threadId}/messages${query}`);
}

export async function createThread(
  message: string,
  context: Record<string, string>,
  attachments?: Pick<ChatAttachment, "signed_id" | "filename" | "content_type">[],
  planModeEnabled?: boolean,
  richTextArtefacts?: RichTextArtefactPayload[],
): Promise<ChatThread> {
  // The model is fixed at organisation level; the server assigns it.
  const body: Record<string, unknown> = { message, context };
  if (attachments?.length) body.attachments = attachments;
  if (richTextArtefacts?.length) body.rich_text_artefacts = richTextArtefacts;
  if (planModeEnabled) body.plan_mode_enabled = true;
  const data = await request<{ thread: ChatThread }>("/chat/threads", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data.thread;
}

export async function sendMessage(
  threadId: string,
  message: string,
  context: Record<string, string>,
  attachments?: Pick<ChatAttachment, "signed_id" | "filename" | "content_type">[],
  richTextArtefacts?: RichTextArtefactPayload[],
): Promise<{ status: string; thread?: ChatThread }> {
  const body: Record<string, unknown> = { message, context };
  if (attachments?.length) body.attachments = attachments;
  if (richTextArtefacts?.length) body.rich_text_artefacts = richTextArtefacts;
  // The server returns the thread (now `queued`) so the store can show the
  // waiting state even if the ActionCable socket is currently down.
  return request(`/chat/threads/${threadId}/messages`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function compactThread(threadId: string): Promise<ChatThread> {
  const data = await request<{ thread: ChatThread }>(`/chat/threads/${threadId}/compact`, {
    method: "POST",
  });
  return data.thread;
}

export async function interruptThread(threadId: string): Promise<ChatThread> {
  const data = await request<{ thread: ChatThread }>(`/chat/threads/${threadId}/interrupt`, {
    method: "POST",
  });
  return data.thread;
}

export async function fetchDebugData(threadId: string): Promise<ChatDebugData> {
  return request(`/chat/threads/${threadId}/debug_data`);
}

export async function fetchTokenResolutions(threadId: string): Promise<TokenResolutions> {
  return request(`/chat/threads/${threadId}/token_resolutions`);
}

export async function updatePlanMode(threadId: string, enabled: boolean): Promise<ChatThread> {
  const data = await request<{ thread: ChatThread }>(`/chat/threads/${threadId}/plan_mode`, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
  return data.thread;
}

export async function approvePlan(
  planId: string,
  mode: "run_all" | "step_by_step",
): Promise<ChatPlan> {
  return request<ChatPlan>(`/chat/plans/${planId}/approve`, {
    method: "POST",
    body: JSON.stringify({ mode }),
  });
}

export async function requestPlanChanges(planId: string, feedback: string): Promise<ChatPlan> {
  return request<ChatPlan>(`/chat/plans/${planId}/request_changes`, {
    method: "POST",
    body: JSON.stringify({ feedback }),
  });
}

export async function rejectPlan(planId: string): Promise<ChatPlan> {
  return request<ChatPlan>(`/chat/plans/${planId}/reject`, { method: "POST" });
}

export async function continuePlan(planId: string): Promise<ChatPlan> {
  return request<ChatPlan>(`/chat/plans/${planId}/continue`, { method: "POST" });
}

export async function cancelPlan(planId: string): Promise<ChatPlan> {
  return request<ChatPlan>(`/chat/plans/${planId}/cancel`, { method: "POST" });
}

export async function answerQuestion(
  questionId: string,
  answer: string | string[],
): Promise<ChatQuestion> {
  return request<ChatQuestion>(`/chat/questions/${questionId}/answer`, {
    method: "POST",
    body: JSON.stringify({ answer }),
  });
}

export async function cancelQuestion(questionId: string): Promise<ChatQuestion> {
  return request<ChatQuestion>(`/chat/questions/${questionId}/cancel`, { method: "POST" });
}
